import React from "react";
import styled from "styled-components";

import { useIntl } from "../../../../shared/core/i18n/use-intl";
import { mandateFilterManager, mandatesManager } from "../../../../shared/core/service/services";
import { type Mandate, MandateStatus } from "../../../../shared/domains/mandates/mandate";
import useMandates from "../../../../shared/domains/mandates/use-mandates";
import { useObservable } from "../../../../shared/utils/observable";
import {
  ACTIVATE_MANDATE_MODAL_ID,
  DETAIL_MANDATE_MODAL_ID,
  OPPOSE_MANDATE_MODAL_ID,
} from "../../../core/modal/modal-id";
import { useRTL } from "../../../domain/language/use-rtl";
import { PrimaryButton } from "../../common/buttons/primary-button";
import { Modal } from "../../common/modal/modal";
import { PageHeader } from "../../common/page/page-header";
import { ActivateMandateModal } from "./components/activate-mandate-modal";
import { DetailMandateModal } from "./components/detail-mandate-modal";
import { FilterBar } from "./components/filter-bar";
import { MandateItem } from "./components/mandate-item";
import { OpposeMandateModal } from "./components/oppose-mandate-modal";

const MandatesScreen = () => {
  const { formatMessage } = useIntl();
  const { mandates, canLoadMore, loadMore, refresh } = useMandates(mandatesManager);
  const { isRTL } = useRTL();
  const mandateStatusSelection = useObservable(mandateFilterManager.statusSelection);

  const handleMandateOppose = (mandate: Mandate) => {
    Modal.present(
      OPPOSE_MANDATE_MODAL_ID,
      () => (
        <OpposeMandateModal
          mandate={mandate}
          onConfirm={async () => {
            await mandatesManager.opposeMandate(mandate.id);
            refresh();
          }}
        />
      ),
      {
        canBeDismissed: true,
      },
    );
  };

  const handleMandateActivate = (mandate: Mandate) => {
    Modal.present(
      ACTIVATE_MANDATE_MODAL_ID,
      () => (
        <ActivateMandateModal
          mandate={mandate}
          onConfirm={async () => {
            await mandatesManager.activateMandate(mandate.id);
            refresh();
          }}
        />
      ),
      {
        canBeDismissed: true,
      },
    );
  };

  const handleMandateClick = (mandate: Mandate) => {
    Modal.present(DETAIL_MANDATE_MODAL_ID, () => <DetailMandateModal mandate={mandate} />, {
      canBeDismissed: false,
    });
  };

  const filteredMandates = () => {
    if (mandateStatusSelection === MandateStatus.ALL) {
      return mandates;
    } else {
      return mandates.filter((mandate) => mandate.status === mandateStatusSelection);
    }
  };
  const filteredMandatesList = filteredMandates();

  return (
    <>
      <PageHeader
        title={formatMessage("mandatesSubmenu.mandates")}
        description={formatMessage("mandates.description")}
      />
      <FilterBar isRTL={isRTL} />
      <MandatesWrapper>
        {filteredMandatesList.map((mandate: Mandate) => (
          <MandateItem
            key={mandate.id}
            mandate={mandate}
            handleMandateOppose={handleMandateOppose}
            handleMandateActivate={handleMandateActivate}
            handleMandateDetail={() => handleMandateClick(mandate)}
          />
        ))}
      </MandatesWrapper>
      {canLoadMore && (
        <LoadMore>
          <PrimaryButton size="S" onClick={loadMore}>
            {formatMessage("upcomingTransfers.loadMore")}
          </PrimaryButton>
        </LoadMore>
      )}
    </>
  );
};

const MandatesWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const LoadMore = styled.div`
  display: flex;
  width: 100%;
  justify-content: center;
  margin-top: 20px;
`;

export default MandatesScreen;
